<template>
    <div class="ari-card" v-if="record" :class="{ 'ari-accepted': approvedFlag === true, 'ari-declined': approvedFlag === false }">
        <div class="ari-left">
            <!-- Employee -->
            <div class="ari-employee">
                <div class="ari-avatar">{{ initials }}</div>
                <div>
                    <router-link :to="`/employees/edit/${record.user && record.user.id}`" class="ari-name">
                        {{ record.user && record.user.firstName }} {{ record.user && record.user.lastName }}
                    </router-link>
                    <div class="ari-meta" v-if="leaveMode">
                        <span v-if="record.paidLeavesLeft != null" class="ari-meta-tag ari-meta-blue">
                            <i class="fas fa-umbrella-beach"></i> {{ record.paidLeavesLeft }} paid leaves left
                        </span>
                        <span class="ari-meta-tag">
                            <i class="fas fa-history"></i> {{ record.totalLeavesAppiledLastMonth }} in last 30 days
                        </span>
                    </div>
                </div>
            </div>

            <!-- Details -->
            <div class="ari-details" v-if="leaveMode">
                <div class="ari-detail-row">
                    <i class="fas fa-calendar-day"></i>
                    <span v-if="formatIndianDate(record.startDate) === formatIndianDate(record.endDate)">
                        {{ formatIndianDate(record.startDate) }}
                    </span>
                    <span v-else>
                        {{ formatIndianDate(record.startDate) }} <span class="ari-arrow">&rarr;</span> {{ formatIndianDate(record.endDate) }}
                    </span>
                </div>
                <div class="ari-detail-row" v-if="record.leaveType">
                    <i class="fas fa-tag"></i>
                    <span class="ari-leave-type">{{ record.leaveType.name }}</span>
                </div>
                <div class="ari-detail-row" v-if="record.reason">
                    <i class="fas fa-comment-alt"></i>
                    <span class="ari-reason">{{ record.reason }}</span>
                </div>
            </div>
            <div class="ari-details" v-else>
                <div class="ari-detail-row">
                    <i class="fas fa-calendar-day"></i>
                    {{ record.date }}/{{ record.month }}/{{ record.year }}
                </div>
                <div class="ari-detail-row" v-if="record.overtimeHours">
                    <i class="fas fa-clock"></i> {{ round2(record.overtimeHours) }} hours overtime
                </div>
                <div class="ari-detail-row" v-if="record.overtimeReason">
                    <i class="fas fa-comment-alt"></i> {{ record.overtimeReason }}
                </div>
            </div>
        </div>

        <!-- Actions -->
        <div class="ari-actions">
            <span v-if="approvedFlag === true" class="ari-status-badge ari-badge-green"><i class="fas fa-check"></i> Accepted</span>
            <span v-else-if="approvedFlag === false" class="ari-status-badge ari-badge-red"><i class="fas fa-times"></i> Declined</span>
            <template v-else>
                <button class="ari-accept-btn" @click="accept" :disabled="accepting">
                    <i :class="accepting ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                    {{ accepting ? '...' : 'Accept' }}
                </button>
                <button class="ari-decline-btn" @click="decline" :disabled="declining">
                    <i :class="declining ? 'fas fa-spinner fa-spin' : 'fas fa-times'"></i>
                    {{ declining ? '...' : 'Decline' }}
                </button>
            </template>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { formatIndianDate, round2 } from "@/utils/common"

export default {
    props: {
        record: { type: Object, default: () => null },
        leaveMode: { type: Boolean, default: false },
    },
    data() {
        return { accepting: false, declining: false }
    },
    computed: {
        approvedFlag() {
            return this.leaveMode ? this.record && this.record.approved : this.record && this.record.overtimeApproved
        },
        initials() {
            const u = this.record && this.record.user
            if (!u) return '?'
            return ((u.firstName || '').charAt(0) + (u.lastName || '').charAt(0)).toUpperCase() || '?'
        },
    },
    methods: {
        round2,
        formatIndianDate,
        async accept() {
            try {
                if (this.approvedFlag !== null) return this.$toast.error("Already processed!")
                if (this.accepting) return
                this.accepting = true
                await http.patch(`/attendance-records/${this.leaveMode ? "leave" : "overtime"}-requests/${this.record.id}/respond`, { approved: true })
                this.record.approved = true
                this.record.overtimeApproved = true
                this.$toast.success("Request accepted!")
            } catch (err) { console.log("error in accepting", err); this.$toast.error("Could not process!") }
            this.accepting = false
        },
        async decline() {
            try {
                if (this.approvedFlag !== null) return this.$toast.error("Already processed!")
                if (this.declining) return
                this.declining = true
                await http.patch(`/attendance-records/${this.leaveMode ? "leave" : "overtime"}-requests/${this.record.id}/respond`, { approved: false })
                this.record.approved = false
                this.record.overtimeApproved = false
                this.$toast.success("Request declined!")
            } catch (err) { console.log("error in declining", err); this.$toast.error("Could not process!") }
            this.declining = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.ari-card { display:flex; flex-direction:column; gap:12px; background:white; border:1px solid $gray100; border-radius:10px; padding:14px 16px; box-shadow:$shadowSm; transition:all $transitionFast; height:100%;
    &:hover { box-shadow:$shadowMd; }
    &.ari-accepted { border-left:3px solid #16a34a; }
    &.ari-declined { border-left:3px solid #dc2626; }
}
.ari-left { flex:1; min-width:0; }

/* Employee */
.ari-employee { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.ari-avatar { width:34px; height:34px; border-radius:$radiusFull; background:linear-gradient(135deg,$primaryColor,lighten($primaryColor,12%)); color:white; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
.ari-name { font-size:13.5px; font-weight:700; color:$gray800; text-decoration:none; &:hover { color:$primaryColor; } }
.ari-meta { display:flex; flex-wrap:wrap; gap:6px; margin-top:3px; }
.ari-meta-tag { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:500; color:$gray500; background:$gray50; padding:2px 8px; border-radius:$radiusFull; i { font-size:10px; } }
.ari-meta-blue { color:$primaryColor; background:$primaryLight; }

/* Details */
.ari-details { display:flex; flex-direction:column; gap:5px; padding-left:44px; }
.ari-detail-row { display:flex; align-items:baseline; gap:7px; font-size:12.5px; color:$gray600; i { width:14px; text-align:center; font-size:11px; color:$gray400; flex-shrink:0; } }
.ari-arrow { color:$gray300; font-size:11px; margin:0 2px; }
.ari-leave-type { font-weight:600; color:$gray700; }
.ari-reason { color:$gray500; font-style:italic; }

/* Actions */
.ari-actions { display:flex; align-items:center; gap:6px; flex-shrink:0; margin-top:auto; padding-top:4px; border-top:1px solid $gray100; justify-content:flex-end; }
.ari-accept-btn, .ari-decline-btn { display:inline-flex; align-items:center; gap:5px; padding:6px 14px; border:none; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit;
    &:disabled { opacity:0.5; cursor:not-allowed; }
}
.ari-accept-btn { background:#dcfce7; color:#16a34a; &:hover:not(:disabled) { background:#bbf7d0; } }
.ari-decline-btn { background:#fee2e2; color:#dc2626; &:hover:not(:disabled) { background:#fecaca; } }

.ari-status-badge { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:$radiusFull; font-size:11.5px; font-weight:600; i { font-size:10px; } }
.ari-badge-green { background:#dcfce7; color:#16a34a; }
.ari-badge-red { background:#fee2e2; color:#dc2626; }

@media(max-width:640px) {
    .ari-details { padding-left:0; }
}
</style>
